import { useEffect, useRef } from "react";

/**
 * Corre `efecto` una vez que `deps` deja de cambiar durante `espera` ms.
 *
 * Reemplaza el patrón de envolver el handler de filtros en `lodash.debounce`
 * dentro de un componente de función: ahí el debounce se reconstruye en cada
 * render, así que cada pulsación estrena su propio temporizador y no agrupa
 * nada, y encima el callback queda cerrado sobre el estado del render que lo
 * creó, de modo que dos cambios seguidos se pisan. Un efecto con setTimeout y
 * su limpieza no tiene ninguno de los dos problemas: React cancela el pendiente
 * antes de volver a correrlo, y cada corrida ve el estado de su propio render.
 *
 * `saltarPrimero` evita la petición extra al montar en las vistas que ya cargan
 * sus datos por su cuenta; las que dependen de este efecto para la carga
 * inicial lo pasan en false y entonces esa primera corrida se ejecuta sin
 * esperar, porque no hay nada que agrupar todavía.
 */
const useDebouncedEffect = (
	efecto,
	deps,
	{ espera = 500, saltarPrimero = true } = {}
) => {
	const primeraCorrida = useRef(true);

	useEffect(() => {
		if (primeraCorrida.current) {
			primeraCorrida.current = false;

			if (saltarPrimero) {
				return;
			}

			// La carga inicial no espera: todavía no hay nada que agrupar y
			// retrasarla medio segundo sólo deja la vista en blanco.
			efecto();

			return;
		}

		const temporizador = setTimeout(efecto, espera);

		return () => clearTimeout(temporizador);
	}, deps);
};

export default useDebouncedEffect;
